import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Clock, ArrowRight, Utensils, Coffee, ChefHat, Sparkles, Heart, Calculator } from 'lucide-react';
import { Button } from '../components/ui/button';
import { images, restaurantInfo, weeklyMenu } from '../data/mock';

const HomePage = () => {
  // Get today's menu
  const today = new Date();
  const dayNames = ['Sunnuntai', 'Maanantai', 'Tiistai', 'Keskiviikko', 'Torstai', 'Perjantai', 'Lauantai'];
  const todayName = dayNames[today.getDay()];
  const todayMenu = weeklyMenu.days.find(d => d.day === todayName) || weeklyMenu.days[0];

  return (
    <div className="min-h-screen overflow-hidden">
      {/* Hero Section - More playful and dynamic */}
      <section className="relative min-h-[90vh] flex items-center bg-gradient-to-br from-emerald-50 via-amber-50 to-emerald-100">
        {/* Decorative elements */}
        <div className="absolute top-20 right-10 w-72 h-72 bg-emerald-200 rounded-full blur-3xl opacity-40 animate-float" />
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-amber-200 rounded-full blur-3xl opacity-30 animate-float delay-500" />
        
        {/* Dotted pattern */}
        <div className="absolute inset-0 dotted-pattern" />
        
        <div className="relative max-w-7xl mx-auto px-6 py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Text content */}
          <div className="animate-fade-in-up">
            {/* Location badge - playful */}
            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm text-emerald-800 px-5 py-2.5 rounded-full text-sm font-medium mb-6 shadow-lg border-2 border-dashed border-emerald-300">
              <MapPin className="w-4 h-4" />
              <span>{restaurantInfo.location}</span>
              <Sparkles className="w-4 h-4 text-green-500" />
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-stone-800 leading-tight mb-4">
              <span className="font-handwritten text-6xl md:text-7xl lg:text-8xl text-emerald-700 block mb-2" style={{lineHeight: 1}}>
                Kotiruokaa
              </span>
              sydämellä
              <Heart className="inline-block w-10 h-10 md:w-12 md:h-12 text-red-500 ml-3 animate-pulse" />
            </h1>

            <p className="text-lg md:text-xl text-stone-600 leading-relaxed mb-6 max-w-lg">
              Tervetuloa kotkalaiseen lounasravintolaan, jossa ruoka tehdään paikan päällä, alusta asti. Oikeista raaka-aineista, rauhassa ja sillä ajatuksella, että ruoan kuuluu maistua hyvältä –
              <span className="font-handwritten text-2xl text-emerald-700"> ihan niin kuin kotona.</span>
            </p>

            {/* Lunch hours - fun style */}
            <div className="inline-flex items-center gap-3 bg-emerald-700 text-white px-6 py-3 rounded-2xl mb-8 shadow-lg transform -rotate-1 hover:rotate-0 transition-transform">
              <Clock className="w-5 h-5" />
              <span className="font-medium">Lounas ma-pe klo {restaurantInfo.lunchHours.weekdays}</span>
            </div>

            {/* CTA Buttons - more playful */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/lounas">
                <Button className="w-full sm:w-auto bg-stone-900 hover:bg-stone-800 text-white px-8 py-6 text-lg rounded-2xl shadow-xl hover:shadow-2xl transition-all group">
                  Katso lounaslista
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/pitopalvelu">
                <Button variant="outline" className="w-full sm:w-auto border-3 border-emerald-400 text-emerald-800 hover:bg-emerald-50 px-8 py-6 text-lg rounded-2xl transition-all">
                  Pitopalvelu
                </Button>
              </Link>
            </div>
          </div>

          {/* Hero Image - playful shape */}
          <div className="relative animate-fade-in-up delay-200">
            <div className="relative">
              {/* Background shape */}
              <div className="absolute -inset-4 bg-gradient-to-br from-emerald-400 to-green-500 rounded-[3rem] rotate-3 opacity-20" />
              <div className="absolute -inset-4 bg-gradient-to-br from-emerald-400 to-green-500 rounded-[3rem] -rotate-2 opacity-10" />
              
              <img
                src={images.hero}
                alt="Ravintola Data"
                className="relative rounded-[2.5rem] shadow-2xl w-full object-cover aspect-[4/3] border-4 border-white"
              />
              
              {/* Floating badge */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4 animate-bounce">
                <p className="font-handwritten text-3xl text-emerald-700">Tervetuloa!</p>
              </div>
              
              {/* Another floating element */}
              <div className="absolute -top-4 -right-4 bg-emerald-500 text-white rounded-full p-4 shadow-xl animate-wiggle">
                <Utensils className="w-6 h-6" />
              </div>
            </div>
          </div>
        </div>

        {/* Wave divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Today's Menu Quick Preview - More fun */}
      <section className="bg-white py-8 relative z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 bg-gradient-to-r from-emerald-50 to-amber-50 rounded-3xl p-6 md:p-8 border-2 border-dashed border-emerald-200 transform hover:-rotate-[0.5deg] transition-transform">
            <div className="flex items-center gap-4">
              <div className="bg-emerald-500 text-white p-4 rounded-2xl shadow-lg animate-wiggle">
                <Utensils className="w-8 h-8" />
              </div>
              <div>
                <p className="text-emerald-700 font-medium mb-1 font-handwritten text-xl">Tänään tarjolla</p>
                <h3 className="text-2xl md:text-3xl font-bold text-stone-800">{todayMenu.main}</h3>
                <p className="text-stone-600 mt-1">+ {todayMenu.soup} ja salaattipöytä</p>
              </div>
            </div>
            <Link to="/lounas">
              <Button className="bg-stone-900 hover:bg-stone-800 text-white px-6 py-3 rounded-xl shadow-lg group">
                Koko viikon lista
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Services Overview - Playful cards */}
      <section className="py-20 md:py-28 bg-white relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-100 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-amber-100 rounded-full translate-y-1/2 -translate-x-1/2" />
        
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="font-handwritten text-4xl text-emerald-500 block mb-2">Meillä saat</span>
            <h2 className="text-4xl md:text-5xl font-bold text-stone-800 mb-4">
              Muutakin kuin lounaan
            </h2>
            <p className="text-lg text-stone-600">
              Kahvihetken tai juhlapöydän tarjottavat – kaikki samalla tutulla tyylillä.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Lunch Card */}
            <Link to="/lounas" className="group">
              <div className="relative overflow-hidden rounded-3xl aspect-[4/3] card-hover bg-white shadow-xl border-2 border-emerald-100">
                <img
                  src={images.hotDishes}
                  alt="Lounas"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-900/90 via-stone-900/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="bg-emerald-500 text-white p-2 rounded-xl">
                      <Utensils className="w-5 h-5" />
                    </div>
                    <span className="font-handwritten text-2xl text-emerald-300">Lounas</span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Päivittäinen lounasbuffet</h3>
                  <p className="text-stone-300 text-sm">Lämmintä kotiruokaa, keitto ja runsas salaattipöytä – joka arkipäivä.</p>
                </div>
              </div>
            </Link>

            {/* Café Card */}
            <Link to="/kahvila" className="group">
              <div className="relative overflow-hidden rounded-3xl aspect-[4/3] card-hover bg-white shadow-xl border-2 border-emerald-100 md:-mt-8">
                <img
                  src={images.kahvila}
                  alt="Kahvila"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-900/90 via-stone-900/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="bg-green-500 text-white p-2 rounded-xl">
                      <Coffee className="w-5 h-5" />
                    </div>
                    <span className="font-handwritten text-2xl text-amber-300">Kahvila</span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Kahvihetki</h3>
                  <p className="text-stone-300 text-sm">Itse leivottuja pullia, tuoreita sämpylöitä ja rauhallinen hetki päivän alkuun.</p>
                </div>
              </div>
            </Link>

            {/* Catering Card */}
            <Link to="/pitopalvelu" className="group">
              <div className="relative overflow-hidden rounded-3xl aspect-[4/3] card-hover bg-white shadow-xl border-2 border-emerald-100">
                <img
                  src={images.catering}
                  alt="Pitopalvelu"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-900/90 via-stone-900/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="bg-rose-500 text-white p-2 rounded-xl">
                      <ChefHat className="w-5 h-5" />
                    </div>
                    <span className="font-handwritten text-2xl text-rose-300">Pitopalvelu</span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Juhla- ja kokoustarjoilu</h3>
                  <p className="text-stone-300 text-sm">Suunnittelemme ja toteutamme tarjoilut tilaisuuden mukaan – rennosti tai juhlavammin.</p>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Story Section - More engaging */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-stone-50 to-emerald-50 relative overflow-hidden">
        {/* Fun shapes */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-emerald-200 rounded-full opacity-50" />
        <div className="absolute bottom-40 right-20 w-32 h-32 bg-amber-200 rounded-full opacity-40" />
        
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="relative">
              {/* Image with playful framing */}
              <div className="relative">
                <div className="absolute -inset-4 bg-emerald-200 rounded-[2rem] rotate-2" />
                <img
                  src={images.saladBuffet}
                  alt="Runsas salaattipöytä"
                  className="relative rounded-[1.5rem] shadow-xl w-full border-4 border-white"
                />
              </div>
              
              {/* Stats badge */}
              <div className="absolute -bottom-8 -right-8 bg-white rounded-2xl shadow-2xl p-6 border-2 border-emerald-100 transform rotate-3 hover:rotate-0 transition-transform">
                <p className="font-handwritten text-5xl text-emerald-700">17+</p>
                <p className="text-stone-600 text-sm font-medium">vuotta Kotkassa</p>
              </div>
            </div>
            
            <div className="lg:pl-8">
              <span className="font-handwritten text-3xl text-emerald-500">Meidän juttu</span>
              <h2 className="text-3xl md:text-4xl font-bold text-stone-800 mb-6 mt-2">
                Ei temppuja – ruoka puhuu puolestaan
              </h2>
              <div className="space-y-4 text-stone-600 leading-relaxed">
                <p>
                  Me uskomme siihen, että hyvä ruoka syntyy <span className="text-emerald-700 font-medium">yksinkertaisista asioista:</span> tuoreista raaka-aineista, osaavista käsistä ja rakkaudesta tekemiseen.
                </p>
                <p>
                  Kun astut sisään, tunnet olevasi <span className="font-handwritten text-2xl text-emerald-700">tervetullut</span>. Keittiöstä leijailee tuttu tuoksu, pöydät on katettu ja ruoka odottaa – juuri sen verran kuin tarvitset.
                </p>
                <p>
                  Tämä ei ole trendiravintola. Tämä on paikka, jonne tullaan syömään <span className="bg-emerald-100 px-2 py-1 rounded-lg">kunnolla</span>. Ruokaa, joka maistuu ja jättää hyvän olon.
                </p>
              </div>
              <Link to="/tarina" className="inline-block mt-8">
                <Button variant="outline" className="border-2 border-stone-300 text-stone-700 hover:bg-emerald-50 hover:border-emerald-300 px-6 py-3 rounded-xl group">
                  Lue tarinamme
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Fun and inviting */}
      <section className="py-20 md:py-28 bg-stone-900 relative overflow-hidden">
        {/* Decorative circles */}
        <div className="absolute top-10 left-10 w-40 h-40 border-4 border-emerald-500/20 rounded-full" />
        <div className="absolute bottom-10 right-10 w-60 h-60 border-4 border-emerald-500/10 rounded-full" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 border-4 border-emerald-500/5 rounded-full" />
        
        <div className="max-w-4xl mx-auto px-6 text-center relative">
          <span className="font-handwritten text-4xl text-emerald-400 block mb-4">Juhlat tulossa?</span>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
            Me hoidamme tarjoilun
          </h2>
          <p className="text-lg text-stone-300 mb-10 max-w-2xl mx-auto">
            Järjestämme pitopalvelun tilaisuuksiisi – oli kyseessä syntymäpäivät, muistotilaisuus tai yrityksen kokous. 
            <span className="font-handwritten text-xl text-emerald-400 block mt-2">Kokeile Buffetkonetta ja saat hinta-arvion!</span>
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/buffetkone">
              <Button className="bg-emerald-500 hover:bg-emerald-700 text-white px-8 py-4 text-lg rounded-2xl shadow-xl hover:shadow-2xl transition-all group">
                Kokeile Buffetkonetta
                <Calculator className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/yhteystiedot">
              <Button variant="outline" className="border-2 border-stone-600 text-stone-200 hover:bg-stone-800 px-8 py-4 text-lg rounded-2xl transition-all">
                Ota yhteyttä
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
